import _ from 'lodash';
import moment from 'moment';
import { mean, median, std, sqrt, round, min } from 'mathjs';
//import { ordered_js, sort_by, js_to_list, series_normalize } from './newFunctions';

export const returns = (x)=>{
    return(x.slice(1).map((k,i)=>(k-x[i])/x[i]));
};

export const returns_json_array = (data, date_id="date") => {
  if (!data || data.length < 2) return [];

  const keys = Object.keys(data[0]).filter(k => k !== date_id);

  return data.slice(1).map((row, i) => {
    const prev = data[i];
    const result = { [date_id]: row[date_id] };

    keys.forEach(k => {
      result[k] = (row[k] - prev[k]) / prev[k];
    });

    return result;
  });
};


export const vol = (x, freq=12)=>{
  if (x.length==0){
    return 0;
  }else{
    return(std(x)*sqrt(freq));
  }

  
};

export const value_at_risk = (x, conf = 0.95) => {
  if (!Array.isArray(x) || x.length === 0) {
    throw new Error("Input must be a non-empty array of returns");
  }
  if (conf <= 0 || conf >= 1) {
    throw new Error("Confidence level must be between 0 and 1");
  }


  // Copy and sort returns ascending
  const sorted = [...x].sort((a, b) => a - b);

  // Percentile index
  const alpha = 1 - conf;
  const idx = Math.floor(alpha * sorted.length);

  // VaR is the quantile (usually expressed as a positive number)
  return -1*sorted[idx];
};

export const avg_ret = (x, freq=12)=>{
  if (x.length==0){
    return null;
  }else{
    return(mean(x)*freq);
  }
};

export const median_ret = (x, freq=12)=>{
    return(median(x)*freq);
};

export const median_of_means = (returns, size = 12, k = null, per = 1) => {
  if (!returns || returns.length === 0) return [];

  // Ensure returns is 2D
  const is1D = typeof returns[0] === "number";
  const data = is1D ? returns.map(v => [v]) : returns;

  const n_rows = data.length;
  const n_cols = data[0].length;

  if (size != null) {
    k = Math.ceil(n_rows / size);
  } else {
    size = Math.ceil(n_rows / k);
  }

  // Collect block means
  const sample_means = Array.from({ length: k }, (_, i) => {
    const start = size * i;
    const end = Math.min(size * (i + 1), n_rows);
    const block = data.slice(start, end);

    return Array.from({ length: n_cols }, (_, j) => {
      const colVals = block.map(row => row[j]);
      return colVals.reduce((a, b) => a + b, 0) / colVals.length;
    });
  });

  // Median across blocks (per column)
  const mom = Array.from({ length: n_cols }, (_, j) => {
    const colMeans = sample_means.map(row => row[j]).sort((a, b) => a - b);
    const mid = Math.floor(colMeans.length / 2);
    const median = colMeans.length % 2 !== 0
      ? colMeans[mid]
      : (colMeans[mid - 1] + colMeans[mid]) / 2;
    return median * per;
  });

  return is1D ? mom[0] : mom;
};

export const sharpe = (x, freq=12)=>{
    if (x.length<4){
      return null;
    }else{
      return sqrt(freq)*mean(x)/std(x);
    }
};

export const total_ret = (x)=>{
    if (x[0]==0){
      return(x.slice(-1));
    }else{
      return(x.slice(-1)/x[0]-1);
    }
};

export const total_ret_annual = (x, value_id, date_id)=>{
    const time_delta =  (new Date(x.slice(-1)[0][date_id]) - new Date(x[0][date_id])) / (1000 * 60 * 60 * 24 * 365.25);
    if (x[0][value_id]==0){
      return((x.slice(-1)[0][value_id])^(1/time_delta)-1);
    }else{
      return((x.slice(-1)[0][value_id]/x[0][value_id])**(1/time_delta)-1);
    }
};

export const cumsum = (x)=>{
    let new_array = [];
    x.reduce(function(a,b,i) { return new_array[i] = a+b; },0);
    return(new_array);
};

export const cumprod = (x)=>{
    let new_array = [];
    x.reduce(function(a,b,i) { return new_array[i] = a*b; },0);
    return(new_array);
};

export const cummax = (x)=>{
    let new_array = [];
    x.reduce(function(a,b,i) { return new_array[i] = Math.max(a,b); },0);
    return(new_array);
};

export const mdd = (wealth)=>{
    let cummax_array = cummax(wealth);
    let drawdown = wealth.map((x,i)=>x/cummax_array[i]-1);
    return (min(drawdown));
};

export function maxDrawdown(series, asset, date_id="Date", targetRec = 1) {
  // series = [{date: "...", asset1: 100, asset2: 90}, ...]
  const prices = series.map(r => r[asset]);
  const dates = series.map(r => new Date(r[date_id]));

  let cummax = [];
  let maxSoFar = -Infinity;

  for (let p of prices) {
    maxSoFar = Math.max(maxSoFar, p);
    cummax.push(maxSoFar);
  }

  const ddRets = prices.map((p, i) => p / cummax[i] - 1);
  const dd = Math.min(...ddRets);

  if (dd >= 0) {
    return {
      maxDrawdown: 0,
      start: null,
      end: null,
      durationMonths: null,
      recoveryMonths: null
    };
  }

  const posEnd = ddRets.indexOf(dd);

  let posIni = 0;
  let maxVal = -Infinity;
  for (let i = 0; i < posEnd; i++) {
    if (cummax[i] > maxVal) {
      maxVal = cummax[i];
      posIni = i;
    }
  }

  const dateIni = dates[posIni];
  const dateLast = dates[posEnd];

  const ddT_days = (dateLast - dateIni) / (1000*60*60*24);
  const ddT = Math.round((ddT_days / 30) * 100) / 100;

  // Recovery target
  const targetRet = (1 - Math.abs(dd) * (1 - targetRec)) / (1 - Math.abs(dd)) - 1;

  const seriesPost = prices.slice(posEnd);
  const datesPost = dates.slice(posEnd);

  const base = seriesPost[0];
  const postReturns = seriesPost.map(p => p / base - 1);

  let recTimeDays = null;
  let recTime = null;

  for (let i = 0; i < postReturns.length; i++) {
    if (postReturns[i] >= targetRet) {
      recTimeDays = (datesPost[i] - dateLast) / (1000*60*60*24);
      recTime = Math.round((recTimeDays / 30) * 100) / 100;
      break;
    }
  }

  return {
    maxDrawdown: Math.round(dd * 100000) / 100000,
    start: dateIni.toISOString().slice(0, 10),
    end: dateLast.toISOString().slice(0, 10),
    durationMonths: ddT,
    recoveryMonths: recTime,
    durationDays:ddT_days,
    recoveryDays: recTimeDays
  };
}
export const series_monthly = (series) => {
  // clone objects so we don’t mutate input
  let series_per = series.map(d => ({
    ...d,
    group_var: d.Date.slice(0, 7),
    day: parseFloat(d.Date.slice(-2))
  }));

  let series_new = [...new Set(series_per.map(x => x.group_var))].map(x => {
    return series_per
      .filter(k => k.group_var === x)
      .filter(k => k.day === Math.max(...series_per
        .filter(k => k.group_var === x)
        .map(k => k.day)))
      .map(k => _.pick(
        k,
        Object.keys(series[0]).filter(k => !['group_var', 'day'].includes(k))
      ))[0];
  });

  return series_new;
};


export const portReturns = (assets_rets, weights) =>{
    if (assets_rets == null || weights == null) {
      return null;
    }
    const port_rets = assets_rets.map(row => {
      let weighted = 0;
      for (const [col, w] of Object.entries(weights)) {
        weighted += (row[col] ?? 0) * w;
      }
      return { Date: row.Date, PortRet: weighted };
    });
    return port_rets;
};

export const portSeries = (port_returns, index_id, ret_id="PortRet") => {
  if (port_returns == null) {
    return null;
  }

  let index = 1.0;
  const portfolioSeries = port_returns.map((r, i) => {
    if (i === 0) {
      return { Date: r.Date, [index_id]: 1.0 }; // always start at 1
    }
    index *= 1 + r[ret_id];
    return { Date: r.Date, [index_id]: index };
  });

  return portfolioSeries;
};


export const get_total_returns = (data, value_id, date_id, factor=1) => {
  if (!data || data.length < 2) return {};

  // Ensure sorted ascending by date
  data = [...data].sort((a, b) => new Date(a[date_id]) - new Date(b[date_id]));

  const latest = data[data.length - 1];
  const latestDate = new Date(latest[date_id]);
  const latestValue = latest[value_id];

  // Periods in months (null for special cases)
  const periods = {
    "1M": 1,
    "3M": 3,
    "6M": 6,
    "YTD": null, // Special case
    "1Y": 12,
    "3Y": 36,
    "5Y": 60
  };

  const results = {};

  for (const [label, months] of Object.entries(periods)) {
    let refDate;

    if (label === "YTD") {
      refDate = new Date(latestDate.getFullYear(), 0, 1);
    } else {
      refDate = new Date(latestDate);
      refDate.setMonth(refDate.getMonth() - months);
    }

    // Skip if reference date is before first available date
    if (refDate < new Date(data[0][date_id])) {
      continue; 
    }

    // Find the closest date in data before or on refDate
    let refPoint = null;
    for (let i = data.length - 1; i >= 0; i--) {
      if (new Date(data[i][date_id]) <= refDate) {
        refPoint = data[i];
        break;
      }
    }

    // Only include if we found a valid point
    if (refPoint) {
      results[label] = factor*(latestValue / refPoint[value_id] - 1);
    }
  }

  return results;
};

export const get_total_returns_windows = (data, value_id, date_id, factor = 1) => {
  if (!data || data.length < 2) return [];

  // Sort ascending by date (deep copy so original is untouched)
  const sortedData = data.map(d => ({ ...d }))
    .sort((a, b) => new Date(a[date_id]) - new Date(b[date_id]));

  const firstDate = new Date(sortedData[0][date_id]);
  const lastDate = new Date(sortedData[sortedData.length - 1][date_id]);
  const firstYear = firstDate.getFullYear();
  const lastYear = lastDate.getFullYear();

  const results = [];

  if (firstYear !== lastYear) {
    // --- Calculate total returns per year ---
    for (let year = firstYear; year <= lastYear; year++) {
      const yearData = sortedData.filter(d => new Date(d[date_id]).getFullYear() === year);
      if (yearData.length > 1) {
        const startVal = yearData[0][value_id];
        const endVal = yearData[yearData.length - 1][value_id];
        results.push({
          name: String(year),
          value: factor * (endVal / startVal - 1)
        });
      }
    }
  } else {
    // --- Calculate total returns per month of that year ---
    for (let month = 0; month < 12; month++) {
      const monthData = sortedData.filter(d => {
        const dDate = new Date(d[date_id]);
        return dDate.getFullYear() === firstYear && dDate.getMonth() === month;
      });
      if (monthData.length > 1) {
        const startVal = monthData[0][value_id];
        const endVal = monthData[monthData.length - 1][value_id];
        results.push({
          name: `${firstYear}-${String(month + 1).padStart(2, "0")}`,
          value: factor * (endVal / startVal - 1)
        });
      }
    }
  }

  return results;
};

function covariance(a, b) {
  const meanA = mean(a);
  const meanB = mean(b);

  let sum = 0;
  for (let i = 0; i < a.length; i++) {
    sum += (a[i] - meanA) * (b[i] - meanB);
  }

  return sum / (a.length-1);
}

export function corr(a, b) {
  return covariance(a, b) / (std(a) * std(b));
}

export function roundToMultiple(value, multiple) {
    return Math.round(value / multiple) * multiple;
}

export function portfolioRound(port, multiple = 0.005, value_key="value") {

    // Copy, round and sort descending
    const rounded = port
        .map(p => ({
            ...p,
            [value_key]: roundToMultiple(p[value_key], multiple)
        }))
        .sort((a, b) => b[value_key] - a[value_key]);

    const total = rounded.reduce((s, p) => s + p[value_key], 0);
    let diff = +(total - 1).toFixed(10);

    if (Math.abs(diff) < 1e-10)
        return rounded;

    if (diff > 0) {
        // Need to remove weight
        for (const asset of rounded) {

            if (diff <= 1e-10)
                break;

            if (asset[value_key] <= multiple)
                continue;

            const removable = Math.max(
                multiple,
                roundToMultiple(asset[value_key] * diff, multiple)
            );

            const adj = Math.min(removable, diff, asset[value_key] - multiple);

            asset.value -= adj;
            diff -= adj;
        }

    } else {

        // Need to add weight
        diff = -diff;

        for (const asset of rounded) {

            if (diff <= 1e-10)
                break;

            const adj = Math.min(
                roundToMultiple(asset.value * diff, multiple),
                diff
            );

            asset[value_key] += adj;
            diff -= adj;
        }
    }

    // Correct any tiny floating-point error
    const finalSum = rounded.reduce((s, p) => s + p[value_key], 0);
    rounded[0][value_key] += 1 - finalSum;

    return rounded;
}